import { Button } from "@/components/ui/button";
import {
  CircularProgress,
  getCoverageScoreClassName,
} from "@/components/ui/circular-progress";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAdminMode } from "@/contexts/admin-mode-provider";
import { useTeams } from "@/contexts/teams-provider";
import { useTranslation } from "@/hooks/useTranslation";
import { RefreshCwIcon, SparklesIcon } from "lucide-react";

type TeamSettingInstructionCoverageDialogProps = {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly coverageScore: number | null;
  readonly coverageReasoning?: string | null;
  readonly onStartConversation?: () => void;
  readonly onForceRecalculate: () => void;
  readonly isEvaluatingScore: boolean;
};

export function TeamSettingInstructionCoverageDialog({
  open,
  onOpenChange,
  coverageScore,
  coverageReasoning,
  onStartConversation,
  onForceRecalculate,
  isEvaluatingScore,
}: TeamSettingInstructionCoverageDialogProps) {
  const { t } = useTranslation();
  const { isAdmin } = useTeams();
  const { isAdminMode } = useAdminMode();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <CircularProgress
              value={coverageScore ?? 0}
              size={36}
              strokeWidth={4}
              showValue={false}
              strokeClassName={
                coverageScore != null
                  ? getCoverageScoreClassName(coverageScore)
                  : undefined
              }
            />
            {t("team.components.instruction.coverage.title", {
              score: coverageScore ?? 0,
            })}
          </DialogTitle>
          <DialogDescription>
            {t("team.components.instruction.coverage.description")}
          </DialogDescription>
        </DialogHeader>
        {coverageReasoning && (
          <div className="whitespace-pre-line text-sm leading-relaxed">
            {coverageReasoning}
          </div>
        )}
        <div className="flex items-center justify-between">
          {isAdmin && isAdminMode ? (
            <Button
              variant="outline"
              size="sm"
              className="border-2 border-warning"
              onClick={() => {
                onOpenChange(false);
                onForceRecalculate();
              }}
              disabled={isEvaluatingScore}
            >
              <RefreshCwIcon className="mr-2 size-4" />
              {t("team.components.instruction.coverage.forceRecalculate")}
            </Button>
          ) : (
            <div />
          )}
          {onStartConversation ? (
            coverageScore === 100 ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onOpenChange(false)}
              >
                {t("team.components.instruction.coverage.closeButton")}
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  onOpenChange(false);
                  onStartConversation();
                }}
              >
                <SparklesIcon className="mr-2 size-4" />
                {t("team.components.instruction.conversation.aiButton")}
              </Button>
            )
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
            >
              {t("team.components.instruction.coverage.closeButton")}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
